import z from 'zod';

import { eventSchema } from '../Event/schemas';
import { groupSchema } from '../Group/schemas';
import { baseTaskSchema } from '../Task/schemas';
import { userSchema } from '../User/schemas';

/** Event compliance status. Possible values: `pending`, `inReview`, `accepted`, `expired`, `expiryWarning`, `exception`. */
const eventStatusSchema = eventSchema.shape.status;

/** User account status. Possible values: `active`, `blocked`, `archived`. */
const userStatusSchema = userSchema.shape.status;

/** User role compliance status. Possible values: `pending`, `accepted`, `expired`, `expiryWarning`, `archived`. */
const userRoleStatusSchema = userSchema.shape.roles.element.shape.status;

/** Company (group) compliance status. Possible values: `pending`, `inReview`, `accepted`, `expired`, `expiryWarning`. */
const groupStatusSchema = groupSchema.shape.status;
const markedForReviewSchema = groupSchema.shape.markedForReview;

export const taskSummarySchema = z.object({
	id: z.string(),
	name: z.string(),
	url: z.string(),
	status: baseTaskSchema.shape.status,
	scope: baseTaskSchema.shape.scope,
});

export const workerRoleSchema = z.object({
	id: z.string(),
	name: z.string(),
	status: userRoleStatusSchema,
	validTo: z.coerce.date().optional(),
	tasks: z.array(taskSummarySchema),
});

export const workerSchema = z.object({
	id: z.string(),
	workforceId: z.string(),
	status: userStatusSchema,
	markedForReview: markedForReviewSchema,
	roles: z.array(workerRoleSchema),
});

const companyStatusSchema = z.object({
	status: groupStatusSchema,
	markedForReview: markedForReviewSchema,
	tasks: z.array(taskSummarySchema),
});

/** Company and worker compliance for a single event (job) assignment. */
const workforceJobAssignmentSchema = z.object({
	company: companyStatusSchema,
	workers: z.array(workerSchema),
});

/** Event compliance entry shared by root and sub-events. */
const workforceJobEventSchema = workforceJobAssignmentSchema.extend({
	eventId: z.string(),
	name: eventSchema.shape.name,
	eventStatus: eventStatusSchema,
	workforceJobId: eventSchema.shape.workforceJobId,
	parentEventId: eventSchema.shape.parentEventId,
});

/**
 * Workforce event status for `GET /api/workforce/:eventId/status`.
 *
 * `root` describes the event identified by the route param.
 * `subEvents` lists nested sub-jobs / sub-events in a flat array (not recursive).
 * Use `parentEventId` on sub-events to reconstruct the hierarchy when needed.
 *
 * @example
 * {
 *   "root": {
 *     "eventId": "event-uuid-1",
 *     "name": "Building renovation",
 *     "eventStatus": "accepted",
 *     "workforceJobId": "wf-job-uuid-1",
 *     "company": {
 *       "status": "accepted",
 *       "markedForReview": ["documents"],
 *       "tasks": [
 *         {
 *           "id": "task-uuid-1",
 *           "name": "HSE agreement",
 *           "url": "/app/manager/tasks/task-uuid-1"
 *         }
 *       ]
 *     },
 *     "workers": [
 *       {
 *         "id": "user-uuid-1",
 *         "workforceId": "WF-12345",
 *         "status": "active",
 *         "markedForReview": [],
 *         "roles": [
 *           {
 *             "id": "role-uuid-1",
 *             "name": "Contractor",
 *             "status": "accepted",
 *             "validTo": "2026-12-31T00:00:00.000Z",
 *             "tasks": [
 *               {
 *                 "id": "task-uuid-2",
 *                 "name": "Safety induction",
 *                 "url": "/app/manager/tasks/task-uuid-2"
 *               }
 *             ]
 *           }
 *         ]
 *       }
 *     ]
 *   },
 *   "subEvents": [
 *     {
 *       "eventId": "event-uuid-2",
 *       "parentEventId": "event-uuid-1",
 *       "name": "Electrical work",
 *       "eventStatus": "inReview",
 *       "workforceJobId": "wf-job-uuid-2",
 *       "company": {
 *         "status": "inReview",
 *         "markedForReview": [],
 *         "tasks": []
 *       },
 *       "workers": []
 *     }
 *   ]
 * }
 */
export const workforceJobStatusSchema = z
	.object({
		root: workforceJobEventSchema,
		subEvents: z.array(workforceJobEventSchema),
	})
	.describe('WorkforceJobStatus');

export {
	taskSummarySchema as workforceJobStatusTaskSummarySchema,
	companyStatusSchema as workforceJobStatusCompanyStatusSchema,
	workerRoleSchema as workforceJobStatusWorkerRoleSchema,
	workerSchema as workforceJobStatusWorkerSchema,
	workforceJobAssignmentSchema as workforceJobStatusAssignmentSchema,
	workforceJobEventSchema,
};
